import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { borderRadius } from '../../../theme/theme';
import type { OperationProgressModel, OperationProgressStatus } from './operationProgress.types';
import {
  getOperationProgressStatusChipColor,
  getOperationProgressStatusLabel,
  isOperationProgressBusy,
} from './operationProgressPresentation';
import { isOperationProgressWaitingState } from './operationProgressUtils';
import {
  OperationProgressBody,
  type OperationProgressSurfaceProps,
} from './OperationProgressBody';

export type { OperationProgressSurfaceProps } from './OperationProgressBody';

export interface OperationProgressWindowProps {
  open: boolean;
  model: OperationProgressModel;
  onClose?: () => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
}

export const OperationProgressWindow: React.FC<OperationProgressWindowProps> = ({
  open,
  model,
  onClose,
  maxWidth = 'sm',
}) => {
  const theme = useTheme();
  const denseWindow = useMediaQuery('(max-width:520px), (max-height:360px)');
  const waiting = model.status === 'waiting' || isOperationProgressWaitingState(model.state);
  const displayStatus: OperationProgressStatus = waiting ? 'waiting' : model.status;
  const locked = model.locked ?? isOperationProgressBusy(displayStatus);
  const titleId = 'operation-progress-window-title';
  const titleTextId = `${titleId}-text`;

  const handleClose = () => {
    if (locked) {
      return;
    }

    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={titleTextId}
      maxWidth={maxWidth}
      fullWidth
      disableEscapeKeyDown={locked}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.82 : 0.68),
            backdropFilter: 'blur(2px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: `${borderRadius.lg}px`,
          border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          boxShadow: `0 18px 48px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.6 : 0.18)}`,
          m: { xs: 1.5, sm: 2 },
          width: { xs: 'calc(100% - 24px)', sm: undefined },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 32px)' },
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: denseWindow ? 1 : 2,
          px: denseWindow ? 1.15 : undefined,
          py: denseWindow ? 0.8 : undefined,
          pb: denseWindow ? 0.6 : 1.25,
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
          <Typography
            id={titleTextId}
            component="span"
            variant={denseWindow ? 'subtitle1' : 'h6'}
            sx={{ display: 'block', fontWeight: 800, lineHeight: 1.2 }}
          >
            {model.title}
          </Typography>
          {model.stepLabel ? (
            <Typography component="span" variant="body2" color="text.secondary">
              {model.stepLabel}
            </Typography>
          ) : null}
        </Box>
        <Chip
          size="small"
          label={getOperationProgressStatusLabel(displayStatus)}
          color={getOperationProgressStatusChipColor(displayStatus)}
          variant="outlined"
          sx={{ flexShrink: 0, height: denseWindow ? 22 : undefined, fontWeight: 800 }}
        />
      </DialogTitle>
      <DialogContent
        sx={{
          pt: 0,
          px: denseWindow ? 1.15 : undefined,
          pb: denseWindow ? 1 : 2.5,
          overflowY: 'auto',
        }}
      >
        <OperationProgressBody model={model} compact={false} framed={false} showTitle={false} testId="operation-progress-window" />
      </DialogContent>
    </Dialog>
  );
};

export const OperationProgressPanel: React.FC<OperationProgressSurfaceProps> = ({
  model,
  compact = false,
  sx,
  testId = 'operation-progress-panel',
}) => (
  <Box
    sx={{
      width: '100%',
      backgroundColor: 'background.paper',
      ...sx,
    }}
  >
    <OperationProgressBody model={model} compact={compact} testId={testId} />
  </Box>
);

export const OperationProgressPlaceholder: React.FC<OperationProgressSurfaceProps> = ({
  model,
  compact = false,
  sx,
  testId = 'operation-progress-placeholder',
}) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      minHeight: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: { xs: 1.5, md: 2.5 },
      py: { xs: 2, md: 2.5 },
      ...sx,
    }}
  >
    <Box sx={{ width: 'min(620px, 100%)' }}>
      <OperationProgressBody model={model} compact={compact} testId={testId} />
    </Box>
  </Box>
);
